import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Autocomplete, TextField, createFilterOptions } from '@mui/material';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY, ALL_UNITS_QUERY, CREATE_INVENTORY_ITEM_MUTATION } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import TrimNameInput from './TrimNameInput';

type LiningType = 'standard' | 'blackout' | 'sheer' | 'none';
type OperatingSystem = 'manual' | 'somfy_standard' | 'rollease';

interface InventoryItem {
  id: number;
  name: string;
  inventoryType: string;
  costCents: number;
  archivedAt: string | null;
  widthInches?: number | null;
}

interface Unit {
  id: number;
  name: string;
  abbreviation: string;
}

interface NewInventoryOption {
  inputValue: string;
  name: string;
  isNew: true;
}

type InventoryOption = InventoryItem | NewInventoryOption;

const filterOptions = createFilterOptions<InventoryOption>();

function formatCost(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

interface State {
  widthInches: string;
  heightInches: string;
  mountType: 'inside' | 'outside' | '';
  spaceAboveWindowInches: string;
  fabricWidthInches: string;
  patternRepeatInches: string;
  fabricDescription: string;
  inventoryItemId: string;
  liningType: LiningType;
  bumpInterlining: boolean;
  trimAlongBottom: boolean;
  trimDescription: string;
  operatingSystem: OperatingSystem;
}

const blank: State = {
  widthInches: '',
  heightInches: '',
  mountType: '',
  spaceAboveWindowInches: '',
  fabricWidthInches: '54',
  patternRepeatInches: '',
  fabricDescription: '',
  inventoryItemId: '',
  liningType: 'standard',
  bumpInterlining: false,
  trimAlongBottom: false,
  trimDescription: '',
  operatingSystem: 'manual',
};

function stateFromInitialValues(iv: ObjectTypeFormProps['initialValues']): State {
  if (!iv) return blank;
  return {
    widthInches: iv.widthInches != null ? String(iv.widthInches) : '',
    heightInches: iv.heightInches != null ? String(iv.heightInches) : '',
    mountType: (iv.mountType as State['mountType']) || '',
    spaceAboveWindowInches: iv.spaceAboveWindowInches != null ? String(iv.spaceAboveWindowInches) : '',
    fabricWidthInches: iv.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54',
    patternRepeatInches: iv.patternRepeatInches != null ? String(iv.patternRepeatInches) : '',
    fabricDescription: iv.fabricDescription || '',
    inventoryItemId: iv.inventoryItemId != null ? String(iv.inventoryItemId) : '',
    liningType: (iv.liningType as LiningType) || 'standard',
    bumpInterlining: iv.bumpInterlining ?? false,
    trimAlongBottom: iv.trimPlacement === 'bottom',
    trimDescription: iv.trimDescription || '',
    operatingSystem: (iv.operatingSystem as OperatingSystem) || 'manual',
  };
}

const BalloonShadeForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>(({ disabled, initialValues }, ref) => {
  const { user } = useAuth();
  const [state, setState] = useState<State>(() => stateFromInitialValues(initialValues));
  const s = (patch: Partial<State>) => setState(prev => ({ ...prev, ...patch }));

  // Tracks the fabric name we last wrote into fabricDescription automatically,
  // so switching inventory items can update it without clobbering a hand-typed value.
  const [autoFilledDescription, setAutoFilledDescription] = useState<string | null>(null);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const [pendingCost, setPendingCost] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data, refetch } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
    fetchPolicy: 'network-only',
  });
  const { data: unitsData } = useQuery(ALL_UNITS_QUERY);
  const [createInventoryItem] = useMutation(CREATE_INVENTORY_ITEM_MUTATION);

  const fabricItems: InventoryItem[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: InventoryItem) => !i.archivedAt && i.inventoryType === 'FABRIC');
  const selectedInventoryItem = fabricItems.find((i) => String(i.id) === state.inventoryItemId) || null;

  const units: Unit[] = unitsData?.allUnits?.nodes || [];
  const yardUnit = units.find((u) => /yard/i.test(u.name) || /^yd/i.test(u.abbreviation));

  // On mount with an existing inventory item selected, treat a description
  // that still matches the item's name as auto-filled so switching items later updates it.
  useEffect(() => {
    if (autoFilledDescription === null && selectedInventoryItem && state.fabricDescription.trim() === selectedInventoryItem.name) {
      setAutoFilledDescription(selectedInventoryItem.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInventoryItem]);

  const selectInventoryItem = (item: InventoryItem) => {
    setState(prev => {
      const isBlank = !prev.fabricDescription.trim();
      const matchesPreviousAutoFill = autoFilledDescription !== null && prev.fabricDescription === autoFilledDescription;
      return {
        ...prev,
        inventoryItemId: String(item.id),
        fabricDescription: isBlank || matchesPreviousAutoFill ? item.name : prev.fabricDescription,
        fabricWidthInches: item.widthInches ? String(item.widthInches) : prev.fabricWidthInches,
      };
    });
    setAutoFilledDescription(item.name);
  };

  const openCreateDialog = (name: string) => {
    setPendingName(name);
    setPendingCost('');
    setCreateError(null);
    setCreateDialogOpen(true);
  };

  const handleCreateConfirm = async () => {
    if (!user?.domain_id || !pendingName.trim()) return;
    if (!yardUnit) {
      setCreateError('No "Yards" unit found — add one in Settings first.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const costCents = Math.round((parseFloat(pendingCost) || 0) * 100);
      const result = await createInventoryItem({
        variables: {
          domainId: user.domain_id,
          name: pendingName.trim(),
          inventoryType: 'FABRIC',
          unitId: yardUnit.id,
          costCents,
          description: null,
          widthInches: null,
          quantityOnHand: 0,
        },
      });
      const newItem = result.data?.createInventoryItem?.inventoryItem;
      await refetch();
      if (newItem?.id) {
        const newName = pendingName.trim();
        setState(prev => {
          const isBlank = !prev.fabricDescription.trim();
          const matchesPreviousAutoFill = autoFilledDescription !== null && prev.fabricDescription === autoFilledDescription;
          return {
            ...prev,
            inventoryItemId: String(newItem.id),
            fabricDescription: isBlank || matchesPreviousAutoFill ? newName : prev.fabricDescription,
          };
        });
        setAutoFilledDescription(newName);
      }
      setCreateDialogOpen(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create inventory item');
    } finally {
      setCreating(false);
    }
  };

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!state.widthInches || !state.heightInches || !state.mountType) return null;
      const w = parseFloat(state.widthInches);
      const h = parseFloat(state.heightInches);
      if (!w || !h) return null;

      return {
        widthInches: w,
        heightInches: h,
        mountType: state.mountType,
        // Only relevant for Outside Mount — see the work-order Finished
        // Length output (Finished Length = Length + Space Above Window).
        spaceAboveWindowInches: state.mountType === 'outside' && state.spaceAboveWindowInches
          ? parseFloat(state.spaceAboveWindowInches) : null,
        fabricWidthInches: state.fabricWidthInches ? parseFloat(state.fabricWidthInches) : 54,
        patternRepeatInches: state.patternRepeatInches ? parseFloat(state.patternRepeatInches) : null,
        fabricDescription: state.fabricDescription.trim() || null,
        inventoryItemId: state.inventoryItemId ? parseInt(state.inventoryItemId, 10) : null,
        liningType: state.liningType,
        bumpInterlining: state.bumpInterlining,
        trimPlacement: state.trimAlongBottom ? 'bottom' : 'none',
        trimDescription: state.trimDescription.trim() || null,
        operatingSystem: state.operatingSystem,
      };
    },
  }));

  return (
    <div style={{ marginTop: 16 }}>
      {/* Dimensions + mount */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div className="form-group" style={{ width: 120 }}>
          <label>Width (in) *</label>
          <input type="number" min="0" step="0.25" className="input" value={state.widthInches}
            onChange={e => s({ widthInches: e.target.value })} disabled={disabled} placeholder="0" />
        </div>
        <div className="form-group" style={{ width: 120 }}>
          <label>Length (in) *</label>
          <input type="number" min="0" step="0.25" className="input" value={state.heightInches}
            onChange={e => s({ heightInches: e.target.value })} disabled={disabled} placeholder="0" />
        </div>
        <div className="form-group">
          <label>Mount Type *</label>
          <select className="input" value={state.mountType}
            onChange={e => s({ mountType: e.target.value as 'inside' | 'outside' })} disabled={disabled}>
            <option value="">Select...</option>
            <option value="inside">Inside Mount (IM)</option>
            <option value="outside">Outside Mount (OM)</option>
          </select>
        </div>
        {state.mountType === 'outside' && (
          <div className="form-group" style={{ width: 160 }}>
            <label>Space Above Window (in)</label>
            <input type="number" min="0" step="0.25" className="input" value={state.spaceAboveWindowInches}
              onChange={e => s({ spaceAboveWindowInches: e.target.value })} disabled={disabled} placeholder="0" />
          </div>
        )}
        <div className="form-group" style={{ width: 120 }}>
          <label>Fabric Width (in)</label>
          <input type="number" min="1" step="0.5" className="input" value={state.fabricWidthInches}
            onChange={e => s({ fabricWidthInches: e.target.value })} disabled={disabled} placeholder="54" />
        </div>
        <div className="form-group" style={{ width: 140 }}>
          <label>Pattern Repeat (in)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.patternRepeatInches}
            onChange={e => s({ patternRepeatInches: e.target.value })} disabled={disabled} placeholder="None" />
        </div>
      </div>

      {/* Fabric */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="form-group" style={{ flex: 2, minWidth: 240 }}>
          <label>Inventory Item <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <Autocomplete<InventoryOption>
            size="small"
            disabled={disabled}
            options={fabricItems}
            value={selectedInventoryItem}
            isOptionEqualToValue={(option, value) => (
              !('isNew' in option) && !('isNew' in value) && option.id === value.id
            )}
            getOptionLabel={(option) => (
              typeof option === 'string' ? option : ('isNew' in option ? option.inputValue : option.name)
            )}
            filterOptions={(options, params) => {
              const filtered = filterOptions(options, params);
              const inputValue = params.inputValue.trim();
              const exists = options.some((o) => !('isNew' in o) && o.name.toLowerCase() === inputValue.toLowerCase());
              if (inputValue !== '' && !exists) {
                filtered.push({ inputValue, name: inputValue, isNew: true });
              }
              return filtered;
            }}
            renderOption={(liProps, option) => (
              <li {...liProps} key={'isNew' in option ? '__new__' : option.id}>
                {'isNew' in option ? `+ Add "${option.inputValue}" as a new fabric` : option.name}
              </li>
            )}
            onChange={(_event, newValue) => {
              if (newValue == null) {
                s({ inventoryItemId: '' });
              } else if ('isNew' in newValue) {
                openCreateDialog(newValue.inputValue);
              } else {
                selectInventoryItem(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search fabric..." />
            )}
          />
        </div>
        {selectedInventoryItem && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>Price</label>
            <div style={{ padding: '9px 0', fontSize: 14, color: '#111827' }}>
              {formatCost(selectedInventoryItem.costCents)}
            </div>
          </div>
        )}
      </div>

      <div className="form-group">
        <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
        <input
          type="text"
          className="input"
          placeholder="e.g., name/color"
          value={state.fabricDescription}
          onChange={(e) => s({ fabricDescription: e.target.value })}
          disabled={disabled}
        />
      </div>

      {/* Lining */}
      <div className="form-group">
        <label>Lining</label>
        <select className="input" value={state.liningType}
          onChange={e => s({ liningType: e.target.value as LiningType })} disabled={disabled}>
          <option value="standard">Regular Lining</option>
          <option value="blackout">Blackout Lining</option>
          <option value="sheer">Sheer Lining</option>
          <option value="none">None</option>
        </select>
      </div>

      {/* Checkboxes + operating system */}
      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div className="form-group">
          <label>Flannel Interlining</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, cursor: 'pointer' }}>
            <input type="checkbox" checked={state.bumpInterlining}
              onChange={e => s({ bumpInterlining: e.target.checked })} disabled={disabled} />
            Add flannel interlining
          </label>
        </div>
        <div className="form-group">
          <label>Trim Along Bottom</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, cursor: 'pointer' }}>
            <input type="checkbox" checked={state.trimAlongBottom}
              onChange={e => s({ trimAlongBottom: e.target.checked })} disabled={disabled} />
            Add bottom trim
          </label>
        </div>
        <TrimNameInput value={state.trimDescription} onChange={(v) => s({ trimDescription: v })} disabled={disabled} />
        <div className="form-group">
          <label>Operating System</label>
          <select className="input" value={state.operatingSystem}
            onChange={e => s({ operatingSystem: e.target.value as OperatingSystem })} disabled={disabled}>
            <option value="manual">Cord &amp; Cleat</option>
            <option value="rollease">Rollease</option>
            <option value="somfy_standard">Somfy Motor</option>
          </select>
        </div>
      </div>

      {createDialogOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 8, width: 420, maxWidth: '90%', boxShadow: '0 20px 25px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>New Fabric</h2>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280' }}>
                &ldquo;{pendingName}&rdquo; isn&apos;t in the inventory yet. Add it as a new fabric?
              </p>
            </div>

            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {createError && (
                <div style={{ padding: 12, background: '#fee2e2', color: '#dc2626', borderRadius: 6, fontSize: 13 }}>
                  {createError}
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Name
                </label>
                <input
                  type="text"
                  className="input"
                  value={pendingName}
                  onChange={(e) => setPendingName(e.target.value)}
                  disabled={creating}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Cost per Yard ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input"
                  placeholder="0.00"
                  value={pendingCost}
                  onChange={(e) => setPendingCost(e.target.value)}
                  disabled={creating}
                />
              </div>

              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                Type: Fabric · Unit: {yardUnit ? yardUnit.name : 'Yards (not found)'} · Qty on hand: 0
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #e5e7eb' }}>
              <button
                style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                onClick={() => setCreateDialogOpen(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                onClick={handleCreateConfirm}
                disabled={creating || !pendingName.trim()}
              >
                {creating ? 'Creating...' : 'Create Fabric'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default BalloonShadeForm;
